import { useMemo } from 'react'

import { useSelector } from 'react-redux'

import { useRecipes } from 'hooks/menu-recipes/useRecipes'
import { useStaticArray } from 'hooks/useStaticArray'
import type { Recipe, RecipeUUID } from 'reducers/menu-recipes/recipes'
import { getBasketRecipes } from 'selectors/basket'

export type BasketRecipe = {
  id: RecipeUUID
  recipe: Recipe | null // null means recipe is not loaded
  quantity: number
}

/**
 * Returns an array of basket recipes with their core IDs and the corresponding recipe objects.
 */
export const useBasketRecipes = () => {
  const basketQuantities = useSelector(getBasketRecipes)
  const basketUUIDs = useMemo(() => Array.from(basketQuantities.keys()), [basketQuantities])
  const recipes = useRecipes(basketUUIDs)
  const staticRecipes = useStaticArray(recipes)

  const basketRecipes = useMemo(
    () =>
      basketUUIDs.map((id, index) => ({
        id,
        recipe: staticRecipes[index] || null,
        quantity: basketQuantities.get(id) ?? 0,
      })),
    [basketUUIDs, staticRecipes, basketQuantities],
  )

  const basketIdsSet = new Set(
    basketRecipes.filter((recipe) => recipe.quantity > 0).map((recipe) => recipe.id),
  )

  const basketHasRecipe = (id: RecipeUUID) => basketIdsSet.has(id)

  return {
    basketRecipes,
    basketHasRecipe,
  }
}
